import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { EXPANDED_THEME_VALUES } from '@wix/wix-vod-constants/app-settings';
import KEY_CODES from '@wix/wix-vod-shared/dist/esm/widget/constants/key-codes';

import { withTranslation } from '@wix/yoshi-flow-editor';
import { memoizedPartial } from '@wix/wix-vod-shared/common';

import {
  getCurrentVideoIndex,
  getVideoIds,
  hasNextVideo,
  hasPrevVideo,
} from '../../../redux/lazy-channel-videos/selectors';

import { selectVideo } from '../../../redux/actions/select-video';
import Nav from '@wix/wix-vod-shared/dist/esm/widget/ui-components/nav/nav';

import styles from './main-item-nav.scss';

const mapStateToProps = (state) => {
  const lazyLoadedVideoIds = getVideoIds(state);
  const currentIdIndex = getCurrentVideoIndex(state);
  const prevVideoId = hasPrevVideo(state)
    ? lazyLoadedVideoIds[currentIdIndex - 1]
    : null;
  const nextVideoId = hasNextVideo(state)
    ? lazyLoadedVideoIds[currentIdIndex + 1]
    : null;
  return { prevVideoId, nextVideoId };
};

const mapDispatchToProps = { selectVideo };

export default withTranslation()(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(
    class VideoInfo extends React.Component {
      static propTypes = {
        selectVideo: PropTypes.func.isRequired,
        mainVideoId: PropTypes.string.isRequired,
        theme: PropTypes.oneOf([
          EXPANDED_THEME_VALUES.DARK,
          EXPANDED_THEME_VALUES.LIGHT,
        ]).isRequired,

        prevVideoId: PropTypes.string,
        nextVideoId: PropTypes.string,
        className: PropTypes.string,

        isRTL: PropTypes.bool,
      };

      componentDidMount() {
        // Not document to don`t focus main item by accessibility feature
        document.body.addEventListener('keydown', this.navigateFromKeyboard);
      }

      componentWillUnmount() {
        document.body.removeEventListener('keydown', this.navigateFromKeyboard);
      }

      navigateFromKeyboard = (event) => {
        const { nextVideoId, prevVideoId } = this.props;

        this.muteKeyboardEvent(event);

        if (event.keyCode === KEY_CODES.LEFT && prevVideoId) {
          this.selectPrevVideo();
          return;
        }

        if (event.keyCode === KEY_CODES.RIGHT && nextVideoId) {
          this.selectNextVideo();
        }
      };

      muteKeyboardEvent = (event) => {
        if (_.includes([KEY_CODES.LEFT, KEY_CODES.RIGHT], event.keyCode)) {
          event.stopPropagation();
          event.preventDefault();

          // Hack to don`t focus main item by accessibility feature
          requestAnimationFrame(() => {
            if (
              document.documentElement.getAttribute('data-focus-source') ===
              'key'
            ) {
              document.documentElement.setAttribute(
                'data-focus-source',
                'script',
              );
            }
          });

          return false;
        }
      };

      selectNextVideo = () => {
        const { nextVideoId } = this.props;
        this.props.selectVideo(nextVideoId);
      };

      selectPrevVideo = () => {
        const { prevVideoId } = this.props;
        this.props.selectVideo(prevVideoId);
      };

      render() {
        const { prevVideoId, nextVideoId, className, theme, isRTL } =
          this.props;

        const classNames = classnames(className, {
          [styles['theme-dark']]: theme === EXPANDED_THEME_VALUES.DARK,
        });

        return (
          <Nav
            className={classNames}
            prevButtonClassName={styles['prev-button']}
            nextButtonClassName={styles['next-button']}
            isPrevVisible={Boolean(prevVideoId)}
            isNextVisible={Boolean(nextVideoId)}
            prevButtonAriaLabel={this.props.t(
              'widget.accessibility.prev-video',
            )}
            nextButtonAriaLabel={this.props.t(
              'widget.accessibility.next-video',
            )}
            onPrevClick={memoizedPartial(this.props.selectVideo, prevVideoId)}
            onNextClick={memoizedPartial(this.props.selectVideo, nextVideoId)}
            dataHook="main-item-nav"
            isRTL={isRTL}
          />
        );
      }
    },
  ),
);
